/**
 * Waste Stream Form Component
 * Form for adding/editing a single waste stream
 */

import React, { useState } from 'react';
import type { WasteStream, WasteStreamType } from '../../types';
import { MaterialSelector } from '../compliance/MaterialSelector';
import { FacilitySelector } from './FacilitySelector';
import { MaterialGuidelines } from '../compliance/MaterialGuidelines';
import { validation } from '../../utils/validation';

interface WasteStreamFormProps {
  initialData?: WasteStream;
  onSubmit: (wasteStream: WasteStream) => void;
  onCancel: () => void;
}

const DIVERSION_METHODS = [
  { value: 'recycle', label: 'Recycle' },
  { value: 'reuse', label: 'Reuse' },
  { value: 'salvage', label: 'Salvage' },
  { value: 'donate', label: 'Donate' },
  { value: 'landfill', label: 'Landfill' },
] as const;

export function WasteStreamForm({ initialData, onSubmit, onCancel }: WasteStreamFormProps) {
  const [formData, setFormData] = useState<WasteStream>(
    initialData || {
      material_type: '' as WasteStreamType,
      estimated_quantity: 0,
      diversion_method: 'recycle',
      facility_name: '',
      facility_address: '',
    }
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const newErrors: Record<string, string> = {};

    if (!formData.material_type) {
      newErrors.material_type = 'Material type is required';
    }

    const quantityError = validation.positiveNumber(
      formData.estimated_quantity,
      'Estimated quantity'
    );
    if (quantityError) {
      newErrors.estimated_quantity = quantityError;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  const handleChange = (
    field: keyof WasteStream,
    value: string | number | WasteStreamType
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <MaterialSelector
        value={formData.material_type}
        onChange={(value) => handleChange('material_type', value)}
        error={errors.material_type}
      />

      <div>
        <label
          htmlFor="estimated-quantity"
          className="block text-sm font-medium text-neutral-700 mb-1"
        >
          Estimated Quantity (tons)
        </label>
        <input
          id="estimated-quantity"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={formData.estimated_quantity || ''}
          onChange={(e) => handleChange('estimated_quantity', parseFloat(e.target.value) || 0)}
          className="input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg"
          required
          aria-describedby={errors.estimated_quantity ? 'quantity-error' : undefined}
        />
        {errors.estimated_quantity && (
          <p id="quantity-error" className="text-sm text-red-600 mt-1" role="alert">
            {errors.estimated_quantity}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="diversion-method"
          className="block text-sm font-medium text-neutral-700 mb-1"
        >
          Diversion Method
        </label>
        <select
          id="diversion-method"
          value={formData.diversion_method}
          onChange={(e) => handleChange('diversion_method', e.target.value)}
          className="input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg"
          required
        >
          {DIVERSION_METHODS.map((method) => (
            <option key={method.value} value={method.value}>
              {method.label}
            </option>
          ))}
        </select>
      </div>

      {/* Material Guidelines */}
      {formData.material_type && (
        <MaterialGuidelines materialType={formData.material_type} />
      )}

      {/* Facility Selector */}
      {formData.material_type && formData.diversion_method !== 'landfill' && (
        <FacilitySelector
          materialType={formData.material_type}
          value={formData.facility_name || ''}
          onChange={(name, address) => {
            handleChange('facility_name', name);
            if (address) {
              handleChange('facility_address', address);
            }
          }}
        />
      )}

      {/* Manual facility input fallback */}
      {(!formData.material_type || formData.diversion_method === 'landfill') && (
        <>
          <div>
            <label
              htmlFor="facility-name"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Facility Name (Optional)
            </label>
            <input
              id="facility-name"
              type="text"
              value={formData.facility_name || ''}
              onChange={(e) => handleChange('facility_name', e.target.value)}
              className="input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg"
              placeholder="Facility name"
            />
          </div>

          <div>
            <label
              htmlFor="facility-address"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Facility Address (Optional)
            </label>
            <input
              id="facility-address"
              type="text"
              value={formData.facility_address || ''}
              onChange={(e) => handleChange('facility_address', e.target.value)}
              className="input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg"
              placeholder="123 Main St, City, CA 12345"
            />
          </div>
        </>
      )}

      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <button type="submit" className="btn-primary flex-1 order-1">
          {initialData ? 'Update Waste Stream' : 'Add Waste Stream'}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary sm:w-auto order-2">
          Cancel
        </button>
      </div>
    </form>
  );
}